// src/pages/MainPanel/CreateExperiment/components/CommandEditor.jsx
import { useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import TerminalOutlinedIcon from "@mui/icons-material/TerminalOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import Editor from "@monaco-editor/react";
import PropTypes from "prop-types";
import {
  EDITOR_THEME_LIGHT_NAME as EDITOR_THEME,
  defineLightEditorTheme,
} from "../../../../theme/codeTheme";
import {
  HAIRLINE,
  HAIRLINE_STRONG,
  SURFACE,
  SURFACE_MUTED,
  INK_SECONDARY,
  INK_MUTED,
  RADIUS,
  FONT,
  WEIGHT,
  STATUS,
} from "../../../../theme/tokens";

export const parseCommands = (text) => {
  const result = [];
  let current = "";

  for (const line of text.split("\n")) {
    if (line.trimEnd().endsWith("\\")) {
      current += line + "\n";
    } else {
      const combined = current + line;
      if (combined.trim()) result.push(combined);
      current = "";
    }
  }

  if (current.trim()) result.push(current.trimEnd());
  return result;
};

const CommandEditor = ({ commands, onCommandsChange, disabled }) => {
  const commandsMissing = !commands.trim();
  const [editorReady, setEditorReady] = useState(false);

  const editorOptions = {
    minimap: { enabled: false },
    lineNumbers: "on",
    wordWrap: "on",
    scrollBeyondLastLine: false,
    fontSize: 13.5,
    fontFamily:
      "'Fira Mono', 'Menlo', 'Monaco', 'Consolas', 'Liberation Mono', monospace",
    tabSize: 2,
    readOnly: disabled,
    renderLineHighlight: "none",
    overviewRulerLanes: 0,
    padding: { top: 12, bottom: 12 },
    scrollbar: { vertical: "auto", alwaysConsumeMouseWheel: false },
  };

  const loader = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
      }}
    >
      <CircularProgress size={20} />
    </Box>
  );

  const borderColor = commandsMissing ? STATUS.error : HAIRLINE;

  return (
    <Box>
      <Box
        sx={{
          border: `1px solid ${borderColor}`,
          borderRadius: `${RADIUS.md}px`,
          overflow: "hidden",
          bgcolor: SURFACE,
          transition: "border-color 0.15s ease",
          "&:focus-within": {
            borderColor: commandsMissing ? STATUS.error : HAIRLINE_STRONG,
          },
        }}
      >
        {/* Header bar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            px: 1.75,
            py: 1,
            bgcolor: SURFACE_MUTED,
            borderBottom: `1px solid ${HAIRLINE}`,
          }}
        >
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}
          >
            <TerminalOutlinedIcon sx={{ fontSize: 16, color: INK_MUTED }} />
            <Typography
              sx={{
                fontSize: FONT.xs,
                fontWeight: WEIGHT.bold,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: INK_SECONDARY,
              }}
            >
              Shell Script
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: FONT.xs,
              color: INK_MUTED,
              display: { xs: "none", sm: "block" },
            }}
          >
            Multi-line supported · end a line with{" "}
            <Box component="code" sx={{ fontFamily: "monospace" }}>
              \
            </Box>{" "}
            to continue
          </Typography>
        </Box>

        {/* Editor body */}
        <Box
          sx={{
            opacity: editorReady ? 1 : 0,
            transition: "opacity 0.35s ease",
          }}
        >
          <Editor
            height="280px"
            language="shell"
            theme={EDITOR_THEME}
            beforeMount={defineLightEditorTheme}
            value={commands}
            onChange={(val) => onCommandsChange(val ?? "")}
            onMount={() => setEditorReady(true)}
            options={editorOptions}
            loading={loader}
          />
        </Box>
      </Box>

      {commandsMissing && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            mt: 0.75,
            color: STATUS.error,
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 15 }} />
          <Typography sx={{ fontSize: FONT.xs }}>
            At least one command is required.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

CommandEditor.propTypes = {
  commands: PropTypes.string.isRequired,
  onCommandsChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default CommandEditor;
